/**
 * successionPositionGrouping.utils.js
 *
 * The succession-tree API returns one node per employee (see
 * GET /talent-mobility/succession-tree). When two or more employees share the
 * same manager AND the same organizational position (job-shared / co-holding
 * a role), rendering one card per employee produces several near-duplicate
 * cards side by side under that manager — cluttered and hard to read. This
 * module regroups same-position siblings into a single "position node" that
 * carries all of its occupants, so the chart renders one card per position
 * with a way to switch between occupants when there's more than one.
 *
 * Grouping is strictly scoped to true siblings — nodes already sitting
 * together in the same `children` array (or the top-level `roots` array) —
 * so employees under different managers, or in unrelated branches, are never
 * merged just because their `current_role` text matches. `context_only`
 * ancestor nodes are never grouped with anything, even same-position peers.
 */

const normalizeRoleKey = (role) => (role || "").trim().toLowerCase().replace(/\s+/g, " ");

// Prefer requisition_number (the real position identifier) — normalized
// current_role is only a fallback for rows missing it.
const positionKeyFor = (node) => {
  const req = (node.requisition_number || "").trim();
  return req ? `req:${req}` : `role:${normalizeRoleKey(node.current_role)}`;
};

// `parentScope` (the parent's employee_id, or "root" at the top level) is
// folded into every generated key so two different managers who each have,
// say, a "Sr. Software Engineer" position never collide in a flat lookup map
// (e.g. the page's selected-employee-per-position / collapsed-position state).
export const buildPositionForest = (rawNodes = [], parentScope = "root") => {
  const groupOrder = [];
  const groupsByKey = new Map();

  rawNodes.forEach((raw) => {
    // context_only nodes are always their own standalone group — never
    // merged with a same-position accessible peer.
    const localKey = raw.context_only ? `ctx:${raw.employee_id}` : positionKeyFor(raw);
    const positionKey = `${parentScope}::${localKey}`;

    let group = groupsByKey.get(positionKey);
    if (!group) {
      group = { positionKey, contextOnly: !!raw.context_only, role: raw.current_role, rawEmployees: [] };
      groupsByKey.set(positionKey, group);
      groupOrder.push(group);
    }
    group.rawEmployees.push(raw);
  });

  return groupOrder.map((group) => ({
    positionKey: group.positionKey,
    contextOnly: group.contextOnly,
    role: group.role,
    employees: group.rawEmployees.map((raw) => ({
      ...raw,
      children: buildPositionForest(raw.children || [], raw.employee_id),
    })),
  }));
};

export const selectEmployeeForNode = (positionNode, selectedEmployeeByPosition) =>
  positionNode.employees.find((e) => e.employee_id === selectedEmployeeByPosition[positionNode.positionKey]) ||
  positionNode.employees[0];

// Every occupant of every position, across the whole forest — used for CSV
// export / counts, independent of which occupant is currently selected in
// any dropdown, so no employee record is ever dropped from those views.
export const flattenAllEmployees = (positionNodes = [], acc = []) => {
  positionNodes.forEach((pos) => {
    pos.employees.forEach((emp) => {
      acc.push(emp);
      if (emp.children?.length) flattenAllEmployees(emp.children, acc);
    });
  });
  return acc;
};

// positionKeys reachable given the *currently selected* occupant at each
// position — what Expand All / Collapse All should actually toggle, since
// only the selected occupant's children ever render.
export const collectExpandablePositionKeys = (positionNodes = [], selectedEmployeeByPosition = {}, acc = []) => {
  positionNodes.forEach((pos) => {
    const selected = selectEmployeeForNode(pos, selectedEmployeeByPosition);
    if (selected.children?.length) {
      acc.push(pos.positionKey);
      collectExpandablePositionKeys(selected.children, selectedEmployeeByPosition, acc);
    }
  });
  return acc;
};
